import {
    OidcClient,
    OidcClientSettings,
    WebStorageStateStore,
    SigninRequest,
    SigninResponse,
    SignoutRequest,
    SignoutResponse } from 'oidc-client';

import { history, storageManager } from 'singletons';
import { PayScaleAgent, unauthorizedError } from 'utils/payscaleagent';
import LocationContext from '@ps/payscale-shared-ui/lib/Types/locationContext';
import AjaxApi from 'utils/ajaxApi';

// !!! exported for testing purposes only!
export const getOidcClientSettings = (origin: string, postLoginUrl: string, postLogoutUrl: string) => {
    const oidcSettings: OidcClientSettings = {
        client_id: 'usermanagement',
        response_type: 'id_token token',
        scope: 'openid profile email payscale',
        authority: window.appConfig.authority,
        metadata: {
            issuer: window.appConfig.authority,
            jwks_uri: `${window.appConfig.authority}/.well-known/openid-configuration/jwks`,
            authorization_endpoint: `${window.appConfig.authority}/connect/authorize`,
            userinfo_endpoint: `${window.appConfig.authority}/connect/userinfo`,
            end_session_endpoint: `${window.appConfig.authority}/connect/endsession`,
            token_endpoint: `${window.appConfig.authority}/connect/token`
        },
        loadUserInfo: false,
        redirect_uri: `${origin}${postLoginUrl}`,
        post_logout_redirect_uri: `${origin}${postLogoutUrl}`
    };

    return oidcSettings;
};

const isAuthError = (e: Error): boolean => {
    return e.message === unauthorizedError;
};

// Object for managing all things auth -- login, logout, the OIDC/oAuth flow,
// as well as provides an interface for making ajax calls which will insert
// the auth token into the header, and process any refresh tokens passed back
class AuthManager implements AjaxApi {
    public static postLoginUrl: string = '/post-login';
    public static postLogoutUrl: string  = '/post-logout';
    private _client: OidcClient;
    private _agent: PayScaleAgent;

    constructor() {
        const settings = getOidcClientSettings(
            history.location().origin,
            AuthManager.postLoginUrl,
            AuthManager.postLogoutUrl);
        this._client = new OidcClient({
            ...settings,
            stateStore: new WebStorageStateStore({
                store: storageManager.sessionStorage
            })
        });

        this._agent = new PayScaleAgent(
            () => this._getToken(),
            token => this._setToken(token));
    }

    // This exists solely for testing/mocking purposes. AuthManager uses this
    // method as proxy to navigate to a new url, since window.location isn't mockable
    static navigateToUrl(url: string): void  {
        window.location.assign(url);
    }

    // returns whether or not a redirect is in progress (redirecting
    // to login page)
    async startup(location: LocationContext): Promise<boolean> {
        if (location.pathname === AuthManager.postLogoutUrl) {
            await this._processOidcLogoutResponse(location);
        } else {
            if (location.pathname === AuthManager.postLoginUrl) {
                await this._processOidcLoginResponse(location);
            }

            // if we don't have or didn't parse a token, redirect to login
            if (!this._isLoggedIn()) {
                await this.login(history.location().href);
                return true;
            }
        }

        return false;
    }

    // perform login handshake with OIDC server.  RedirectURL is the URL
    // to navigate to upon successful login, for example if the user navigates
    // to a direct URL but isn't logged in, or the session times out when they're
    // on anything other than the home page
    async login(redirectUrl?: string): Promise<void> {
        if (this._isLoggedIn()) {
            return;
        }

        await this._client.createSigninRequest({state: redirectUrl})
            .then((signinRequest: SigninRequest) => {
                    AuthManager.navigateToUrl(signinRequest.url);
                });
    }

    async logout(): Promise<void> {
        if (!this._isLoggedIn()) {
            return;
        }

        storageManager.jwt.remove();
        await this._client.createSignoutRequest()
            .then((signoutRequest: SignoutRequest) => {
                   AuthManager.navigateToUrl(signoutRequest.url);
                })
            .catch(err => {
                // if we got an error trying to logout, force a reload
                // to resync our state, etc
                AuthManager.navigateToUrl('/');
            });
    }

    // Implementation of ajax API which will insert any auth token into
    // the header, as well as handle any token/auth errors
    get<T>(url: string): Promise<T> {
        return this._agent ?
            this._agent.get(url).catch((e: Error) => {
                if (isAuthError(e)) {
                    storageManager.jwt.remove();
                    this.login(history.location().href);
                }

                return Promise.reject(null);
            }) as Promise<T> :
            Promise.reject(null);
    }

    // Implementation of ajax API which will insert any auth token into
    // the header, as well as process fresh tokens handed back and handle
    // any token/auth errors
    post<T>(url: string, bodyData: object): Promise<T> {
        return this._agent ?
            this._agent.post(url, bodyData).catch((e: Error) => {
                if (isAuthError(e)) {
                    storageManager.jwt.remove();
                    this.login(history.location().href);
                }

                return Promise.reject(null);
            }) as Promise<T> :
            Promise.reject(null);
    }

    // Implementation of ajax API which will insert any auth token into
    // the header, as well as process fresh tokens handed back and handle
    // any token/auth errors
    delete<T>(url: string, bodyData: object): Promise<T> {
        return this._agent ?
            this._agent.delete(url, bodyData).catch((e: Error) => {
                if (isAuthError(e)) {
                    storageManager.jwt.remove();
                    this.login(history.location().href);
                }

                return Promise.reject(null);
            }) as Promise<T> :
            Promise.reject(null);
    }

    put<T>(url: string, bodyData: object): Promise<T> {
        return this._agent ?
            this._agent.put(url, bodyData).catch((e: Error) => {
                if (isAuthError(e)) {
                    storageManager.jwt.remove();
                    this.login(history.location().href);
                }

                return Promise.reject(null);
            }) as Promise<T> :
            Promise.reject(null);
    }

    // Returns the access token stored off from login
    private _getToken(): string | null {
        return storageManager.jwt.get();
    }

    // Sets the access token used for authentication.
    private _setToken(token: string): void {
        storageManager.jwt.set(token);
    }

    private _isLoggedIn(): boolean {
        return !!this._getToken();
    }

    // Processes the provided URL/location under the assumption it reflects
    // a redirect from an OIDC server and includes tokens and other login
    // information.  Stores off any tokens, and updates the current url
    private async _processOidcLoginResponse(location: LocationContext): Promise<void> {
        // To avoid wasting time waiting for the oidc client to load, assume
        // the knowledge here that the client can only do something useful here
        // if the url has a hash
        if (location.hash.length <= 1) {
            return;
        }

        await this._client.processSigninResponse(location.hash)
            .then((response: SigninResponse) => {
                this._setToken(response.access_token);
                history.replace(response.state || '/'); // remove hash from URL
            })
            .catch(() => {
                // something went wrong, but not much we can do here
                history.replace('/');
            });
    }

    private async _processOidcLogoutResponse(location: LocationContext): Promise<void> {
        // To avoid wasting time waiting for the oidc client to load, assume
        // the knowledge here that the client can only do something useful here
        // if the url has a hash
        if (location.hash.length <= 1) {
            return;
        }

        await this._client.processSignoutResponse(location.hash)
            .then((response: SignoutResponse) => {
                history.replace(
                    response.state ||
                    location.href.substr(0, location.href.length - location.hash.length)); // remove hash from URL
            })
            .catch(() => {
                // something went wrong, but not much we can do here
                history.replace('/logout');
            });
    }
}

export { AuthManager };
export default AuthManager;



// WEBPACK FOOTER //
// ./src/utils/authManager.ts